import { inPeriod, validatePeriod } from './performanceModel';
import { toLocalIsoDate } from '../utils/localDate';

export const FINANCE_ACTIVITIES = { agricultura: 'Agricultura', pecuaria: 'Pecuária', geral: 'Uso compartilhado' };
export const FINANCE_KINDS = { entrada: 'Entrada — valor recebido', saida: 'Saída — valor pago' };
export const FINANCE_CATEGORIES = {
  operacao: 'Atividade: vendas, insumos, serviços, salários, impostos ou juros',
  investimento: 'Bens: compra ou venda de máquinas, instalações e outros bens',
  financiamento: 'Empréstimo: recebimento ou devolução do principal (sem juros)',
  capital: 'Proprietário: aporte ou retirada de dinheiro',
  transferencia: 'Transferência entre contas da própria propriedade',
};

export function moneyCents(value, allowZero = true) {
  if (value == null || !/^\d+(\.\d{1,2})?$/.test(String(value))) return null;
  const cents = Math.round(Number(value) * 100);
  return Number.isSafeInteger(cents) && cents >= (allowZero ? 0 : 1) && cents < 100000000000000 ? cents : null;
}
export function allocationPercent(value) {
  const cents = moneyCents(value);
  return cents != null && cents <= 10000 ? cents : null;
}
export function validateFinanceMovement(form, allowUnclassified = false) {
  if (!form || !Object.keys(FINANCE_KINDS).includes(form.tipo) || !Object.keys(FINANCE_ACTIVITIES).includes(form.atividade)) return 'Escolha entrada ou saída e a atividade do lançamento.';
  if (validatePeriod({ start: form.data, end: form.data }) || form.data > toLocalIsoDate()) return 'Informe a data real do recebimento ou pagamento, não futura.';
  if (String(form.descricao || '').trim().length < 3 || String(form.descricao).length > 500) return 'Descreva o lançamento com 3 a 500 caracteres.';
  if (moneyCents(form.valor, false) == null) return 'Informe o valor recebido ou pago, maior que zero e com até duas casas decimais.';
  if (!Object.keys(FINANCE_CATEGORIES).includes(form.categoria) && !(allowUnclassified && (form.categoria == null || form.categoria === ''))) return 'Informe de que é este valor para não confundir empréstimo ou investimento com lucro.';
  if (form.atividade === 'geral' && form.percentual_agricultura != null && form.percentual_agricultura !== ''
    && allocationPercent(form.percentual_agricultura) == null) return 'A parte da Agricultura deve ficar entre 0% e 100%.';
  if (form.atividade !== 'geral' && form.percentual_agricultura != null && form.percentual_agricultura !== '') return 'A divisão é apenas para uso compartilhado.';
  return '';
}
const bucket = () => ({ incoming: 0, outgoing: 0, count: 0, pending: 0, unclassified: 0 });
function metrics(value, unavailable = false) {
  const available = !unavailable && value.count > 0;
  return {
    incoming: available ? value.incoming / 100 : null,
    outgoing: available ? value.outgoing / 100 : null,
    result: available ? (value.incoming - value.outgoing) / 100 : null,
    count: value.count,
  };
}
function indicators(value) {
  const amounts = metrics(value, value.pending > 0);
  return {
    received: amounts.incoming, paid: amounts.outgoing, estimatedProfit: amounts.result, pendingCount: value.pending, unclassifiedCount: value.unclassified,
    margin: amounts.result != null && amounts.incoming > 0 ? (value.incoming - value.outgoing) / value.incoming * 100 : null,
    returnOnCosts: amounts.result != null && amounts.outgoing > 0 ? (value.incoming - value.outgoing) / value.outgoing * 100 : null,
  };
}
function agricultureShare(cents, percent) {
  return Math.floor(cents / 10000) * percent + Math.round((cents % 10000) * percent / 10000);
}
export function summarizeFinancialMovements(movements, period) {
  const emptyActivity = () => ({ ...metrics(bucket()), indicators: indicators(bucket()) });
  const empty = error => ({ ...emptyActivity(), activities: { agricultura: emptyActivity(), pecuaria: emptyActivity() }, rows: [], unallocatedCount: 0, unclassifiedCount: 0, unallocatedOperatingCount: 0, error });
  if (!period || validatePeriod(period)) return empty('Escolha um período válido para consultar as entradas e saídas.');
  const active = movements.filter(item => !item?.estornado_em);
  if (active.some(item => !item?.id || validateFinanceMovement(item, true))) return empty('Há um recebimento ou pagamento inválido. Confira os registros antes de usar as métricas.');
  if (new Set(active.map(item => item.id)).size !== active.length) return empty('A consulta trouxe lançamentos repetidos. Atualize os dados antes de usar as métricas.');
  const rows = active.filter(item => inPeriod(item.data, period)).sort((a, b) => b.data.localeCompare(a.data) || String(a.id).localeCompare(String(b.id)));
  const total = bucket();
  const activities = { agricultura: bucket(), pecuaria: bucket() };
  const operation = bucket();
  const operatingActivities = { agricultura: bucket(), pecuaria: bucket() };
  let unallocatedCount = 0;
  let unclassifiedCount = 0;
  let unallocatedOperatingCount = 0;
  for (const item of rows) {
    if (item.categoria === 'transferencia') continue;
    const key = item.tipo === 'entrada' ? 'incoming' : 'outgoing';
    const cents = moneyCents(item.valor, false);
    total[key] += cents; total.count += 1;
    if (item.atividade === 'geral') {
      const percent = allocationPercent(item.percentual_agricultura);
      if (percent == null) unallocatedCount += 1;
      else {
        const agriculture = agricultureShare(cents, percent);
        activities.agricultura[key] += agriculture;
        activities.pecuaria[key] += cents - agriculture;
        activities.agricultura.count += 1; activities.pecuaria.count += 1;
      }
    } else {
      activities[item.atividade][key] += cents; activities[item.atividade].count += 1;
    }
    if (!item.categoria) {
      unclassifiedCount += 1; operation.pending += 1; operation.unclassified += 1;
      const affected = item.atividade === 'geral' ? Object.values(operatingActivities) : [operatingActivities[item.atividade]];
      affected.forEach(value => { value.pending += 1; value.unclassified += 1; });
    } else if (item.categoria === 'operacao') {
      operation[key] += cents; operation.count += 1;
      if (item.atividade === 'geral') {
        const percent = allocationPercent(item.percentual_agricultura);
        if (percent == null) {
          unallocatedOperatingCount += 1;
          Object.values(operatingActivities).forEach(value => { value.pending += 1; });
        } else {
          const agriculture = agricultureShare(cents, percent);
          operatingActivities.agricultura[key] += agriculture;
          operatingActivities.pecuaria[key] += cents - agriculture;
          operatingActivities.agricultura.count += 1; operatingActivities.pecuaria.count += 1;
        }
      } else {
        operatingActivities[item.atividade][key] += cents; operatingActivities[item.atividade].count += 1;
      }
    }
  }
  if ([total, operation, ...Object.values(activities), ...Object.values(operatingActivities)].some(item => !Number.isSafeInteger(item.incoming) || !Number.isSafeInteger(item.outgoing))) {
    return empty('Os valores ultrapassam o limite seguro de cálculo. Revise os lançamentos.');
  }
  return {
    ...metrics(total), indicators: indicators(operation),
    activities: Object.fromEntries(Object.entries(activities).map(([key, value]) => [key, { ...metrics(value, unallocatedCount > 0), indicators: indicators(operatingActivities[key]) }])),
    rows, unallocatedCount, unclassifiedCount, unallocatedOperatingCount, error: '',
  };
}
